/**
 * Shared backend-verified-poll mechanics for a Purchase, extracted out of
 * `useCreatePurchase.ts` so the single "Buy Now" flow and Cart's per-order
 * payment flow (`useCartCheckout.ts`) never carry two independently-drifting
 * copies of the poll loop, interval/attempt-cap constants, or the
 * payment-failed copy — a code-review finding on the first version of this
 * extraction: only `classifyPurchaseOutcome` (`purchaseOutcome.ts`) had been
 * shared, while the ~40 lines around it (the loop, the constants) stayed
 * duplicated and had already drifted.
 *
 * `isStillCurrent` is a caller-supplied guard (checked before AND after each
 * network call) so a superseded attempt (component unmounted, a newer
 * payForOrder/openPayment call started) stops polling and returns `null`
 * without writing any state — callers must treat `null` as "do nothing,"
 * never as any of the real outcomes.
 */
import { getPurchase } from './api';
import { classifyPurchaseOutcome } from './purchaseOutcome';
import type { Purchase } from './types';

export const POLL_INTERVAL_MS = 3000;
export const MAX_POLL_ATTEMPTS = 20; // ~60s — generous for a webhook round trip, bounded so the UI never spins forever
export const PAYMENT_FAILED_MESSAGE = 'Your payment could not be completed. Please try again.';

/** Checkout.js never loaded (ad-blocker, corporate network, offline) — nothing was charged, so don't say the payment failed. */
export const PAYMENT_UNAVAILABLE_MESSAGE =
  'We couldn\'t load the secure payment window, so you have not been charged. Please disable any ad-blocker or try another network, then try again.';

export type ResolvedOutcome = { kind: 'paid' | 'payment-failed'; purchase: Purchase };
export type PollResult = ResolvedOutcome | { kind: 'confirmation-pending' };

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Polls until the Purchase resolves to a real outcome, gives up (returns
 * `confirmation-pending` — never a guessed success/failure) after
 * `MAX_POLL_ATTEMPTS`, or the caller signals it is no longer current
 * (returns `null`).
 */
export async function pollPurchaseUntilResolved(
  purchaseId: string,
  isStillCurrent: () => boolean,
): Promise<PollResult | null> {
  for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt += 1) {
    await sleep(POLL_INTERVAL_MS);
    if (!isStillCurrent()) return null;
    let latest: Purchase;
    try {
      latest = await getPurchase(purchaseId);
    } catch {
      continue; // transient network error — keep polling, never surface as a hard failure
    }
    if (!isStillCurrent()) return null;
    const outcome = classifyPurchaseOutcome(latest.status);
    if (outcome) return { kind: outcome, purchase: latest };
  }
  if (!isStillCurrent()) return null;
  return { kind: 'confirmation-pending' };
}

/**
 * One manual, unconditional fetch (no polling, no attempt cap) — for a
 * "Check status" control from `confirmation-pending`. Returns `null` when
 * the purchase is still genuinely pending (caller keeps its current state,
 * never guesses); propagates a network error to the caller rather than
 * swallowing it, so each caller can decide its own best-effort handling.
 */
export async function fetchPurchaseOutcomeOnce(purchaseId: string): Promise<ResolvedOutcome | null> {
  const latest = await getPurchase(purchaseId);
  const outcome = classifyPurchaseOutcome(latest.status);
  if (!outcome) return null;
  return { kind: outcome, purchase: latest };
}
